/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Autosuggestions } from '@msdyn365-commerce/commerce-entities';
import MsDyn365, { getCatalogId, getUrlSync } from '@msdyn365-commerce/core';
import { ProductRefiner, ProductRefinerSource, ProductsDataActions } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, ObjectExtensions, Random, validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { createSearchStateInput } from '@msdyn365-commerce-modules/search-utilities';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, KeyCodes } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react';
import * as React from 'react';
import { ICartState } from '@msdyn365-commerce/global-state';
import getSearchSuggestionsAction, { AutoSuggestInput, SuggestionType } from './actions/get-auto-suggest-suggestions';
import {
    CategorySuggestionsComponent,
    FormComponent,
    ISearchCategoryViewProps,
    ISearchKeywordViewProps,
    ISearchProductViewProps,
    KeywordSuggestionsComponent,
    ProductSuggestionsComponent
    // LanguageSelector
} from './components';
import { ISearchData } from './search.data';
import { ISearchProps, ISuggestionTypeCriterionData, SuggestionTypeCriterionSuggestionType } from './search.props.autogenerated';
import { Loader } from '@googlemaps/js-api-loader';

export interface ISearchState {
    searchText: string;
    searchKeywordSuggestClass: string;
    searchProductSuggestClass: string;
    searchCategorySuggestClass: string;
    suggestions: Autosuggestions | undefined;
    searchRefiners?: ProductRefiner[] | undefined;
    autoSuggestAriaLabel: string;
    isInitialState: boolean;
    isLoadingAutoSuggest?: boolean;
    isModalOpen?: boolean;
    selectedMarker?: any;
    mapApiKey: string;
    addressBarDetails: string;
    activeModalContent: string;
    selectedAddress: number;
    isEligibleForQD: boolean;
    addressLatLng: any;
    polygonsData: any[];
}

export interface ISearchViewProps extends ISearchProps<ISearchData> {
    Search: IModuleProps;
    AutoSuggestAriaLabel: INodeProps;
    AutoSuggestAriaLabelText: string;
    searchText: string;
    AutoSuggest: INodeProps;
    KeywordSuggest: INodeProps;
    ProductSuggest: INodeProps;
    CategorySuggest: INodeProps;
    SearchForm: INodeProps;
    FormWrapper: INodeProps;
    UlKeyword: INodeProps;
    UlProduct: INodeProps;
    UlCategory: INodeProps;
    form: React.ReactNode;
    languageSelector?: React.ReactNode;
    autosuggestKeyword?: ISearchKeywordViewProps;
    autosuggestProduct?: ISearchProductViewProps;
    autosuggestCategory?: ISearchCategoryViewProps;
    callbacks: {
        handleCancelSearchChange(): void;
        handleCancelSearchFocused?(): void;
    };
    isLoadingAutoSuggest?: boolean;
    isLoadingNode?: React.ReactNode;
    isModalOpen?: boolean;
    selectedMarker?: any;
    mapApiKey: string;
    toggleMapModal: () => void;
    setSelectedMarker: (marker: any) => void;
    setAddressBarDetails: (marker: any) => void;
    isEligibleForQuickDelivery: (lat: number, lng: number) => void;
    addressBarDetails: string;
    activeModalContent: string;
    setActiveModalContent: (data: string) => void;
    selectedAddress: any;
    addressLatLng: any;
    setSelectedAddress: (data: any) => void;
    setAddressLatLng: (data: any) => void;
    isEligibleForQD: boolean;
    polygonsData: any[];
    cart?: ICartState | undefined;
    userToken?: string;
}

/**
 *
 * Search module.
 * @extends {React.Component<ISearchProps<ISearchData>, ISearchState>}
 */
class Search extends React.Component<ISearchProps<ISearchData>, ISearchState> {
    private readonly searchTextInput: React.RefObject<HTMLInputElement>;

    private readonly formReference: React.RefObject<HTMLButtonElement>;

    private readonly maxChars: number;

    private readonly topResultsCount?: number;

    private readonly hitPrefix: string = '{';

    private readonly hitSuffix: string = '}';

    private readonly waitTime: number = 500;

    private readonly autoSuggestResultScreenReaderDiv: React.RefObject<HTMLDivElement>;

    private readonly autoSuggestResultDiv: React.RefObject<HTMLDivElement>;

    private readonly searchFormClass: string;

    private readonly telemetryContent: ITelemetryContent;

    private loadSuggestionsRequestId?: string;

    private inputSuggestionType: ISuggestionTypeCriterionData[];

    // @ts-expect-error
    private cancellabelDebounce: debounce;

    public constructor(props: ISearchProps<ISearchData>) {
        super(props);
        this.searchTextInput = React.createRef();
        this.autoSuggestResultScreenReaderDiv = React.createRef();
        this.autoSuggestResultDiv = React.createRef();
        this.formReference = React.createRef();

        this.state = {
            searchText: '',
            searchKeywordSuggestClass: 'ms-search__autoSuggest__keyword',
            searchProductSuggestClass: 'ms-search__autoSuggest__product',
            searchCategorySuggestClass: 'ms-search__autoSuggest__category',
            suggestions: undefined,
            searchRefiners: undefined,
            autoSuggestAriaLabel: '',
            isInitialState: true,
            isLoadingAutoSuggest: false,
            isModalOpen: false,
            selectedMarker: '',
            mapApiKey: 'AIzaSyDv7IRtei-JwJykFjK8BaOg-pt_CrWUmvs',
            addressBarDetails: 'Select location',
            activeModalContent: 'location',
            selectedAddress: 0,
            isEligibleForQD: false,
            addressLatLng: {},
            polygonsData: []
        };
        this.maxChars =
            (this.props.context &&
                this.props.context.app &&
                this.props.context.app.config &&
                this.props.context.app.config.searchInputMaxLength) ||
            50;

        this.inputSuggestionType = [];
        this.topResultsCount = this.props.config && this.props.config.topResultsCount ? this.props.config.topResultsCount : 5;
        this.searchFormClass = 'ms-search__form';
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.toggleMapModal = this.toggleMapModal.bind(this);
        this.setAddressBarDetails = this.setAddressBarDetails.bind(this);
        this.isEligibleForQuickDelivery = this.isEligibleForQuickDelivery.bind(this);
        this.setSelectedMarker = this.setSelectedMarker.bind(this);
        this.setActiveModalContent = this.setActiveModalContent.bind(this);
        this.setSelectedAddress = this.setSelectedAddress.bind(this);
        this.setAddressLatLng = this.setAddressLatLng.bind(this);
        this._initSuggestionTypes();
    }

    public async componentDidMount(): Promise<void> {
        // Fetch polygon data from CMS
        await this.fetchPolygonData();
        document.body && document.body.addEventListener('mousedown', this._focusOutTarget);
        window && window.addEventListener('keyup', this._keyup);
        const addBarLatLngObj = localStorage?.getItem('address-lat-lng');
        addBarLatLngObj && this.setState({ addressLatLng: addBarLatLngObj });
        const addBarLatLng = addBarLatLngObj ? JSON.parse(addBarLatLngObj) : '';
        if (addBarLatLng) {
            await this.isEligibleForQuickDelivery(addBarLatLng.lat, addBarLatLng.lng);
        }

        const locationDetails = localStorage.getItem('delivery-location');
        locationDetails && this.setState({ selectedMarker: JSON.parse(locationDetails) });

        // Save in state selected address record id from LS
        const selectAddRecordId = localStorage?.getItem('selected-address');
        const addBarDetails = localStorage?.getItem('address-bar-details');
        //Remove this in next deployment
        const addBarDetailsRefreshStatus = localStorage?.getItem('address-refreshed-status');
        addBarDetails && this.setState({ addressBarDetails: addBarDetails });
        selectAddRecordId && this.setState({ selectedAddress: Number(selectAddRecordId) });
        // Show Nearest address location pin details if no saved address in LS & if no location pin selected by user
        if (
            (this.props.data.address.result && this.props.data.address.result.length > 0 && !selectAddRecordId && !addBarDetails) ||
            //Remove this in next deployment
            !addBarDetailsRefreshStatus
        ) {
            if (navigator && navigator?.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    async position => {
                        // Calculate the distance between the user and each address
                        const sortedAddressObject =
                            this.props.data.address?.result &&
                            this.props.data.address?.result.map((addr: any) => {
                                const latLng = addr.DistrictName.split(',');
                                const dist = this.fetchDistance(latLng[0], latLng[1], position.coords.latitude, position.coords.longitude);
                                return { distance: dist, ...addr };
                            });

                        // Sort by distance nearest address
                        sortedAddressObject &&
                            sortedAddressObject.sort(
                                (a: { distance: string }, b: { distance: string }) => parseFloat(a.distance) - parseFloat(b.distance)
                            );

                        // console.log('thiss Distance (km): ', sortedAddressObject);
                        if (sortedAddressObject && sortedAddressObject[0]) {
                            const addressType =
                                this.props.data.addressPurposes?.result &&
                                this.props.data.addressPurposes?.result.filter(
                                    (addressTypeValue: { AddressType: any }) =>
                                        addressTypeValue.AddressType === sortedAddressObject[0].AddressTypeValue
                                );
                            const addressTypeValue = addressType && addressType.length > 0 ? addressType[0].Description : '';
                            this.setActiveModalContent('address');
                            this.setAddressBarDetails(`${addressTypeValue} - ${sortedAddressObject[0].Street}`);
                            const latLngtObject = sortedAddressObject[0].DistrictName && sortedAddressObject[0].DistrictName.split(',');
                            const latitude = latLngtObject && latLngtObject.length > 0 ? Number(latLngtObject[0]) : 0;
                            const longitutude = latLngtObject && latLngtObject.length > 0 ? Number(latLngtObject[1]) : 0;
                            await this.isEligibleForQuickDelivery(latitude, longitutude);
                            this.setAddressLatLng(JSON.stringify({ lat: latitude, lng: longitutude }));
                            this.setSelectedAddress(sortedAddressObject[0].RecordId);
                        }
                    },
                    error => {
                        console.error('Error getting user location:', error);
                    }
                );
            }
        }

        //RePatch the address here if its already saved/selected & shippingAddress isnt already added to cart
        if (
            this.props.data.address.result &&
            this.props.data.address.result.length > 0 &&
            selectAddRecordId &&
            !this.props.data?.cart?.result?.cart?.ShippingAddress
        ) {
            console.log('delivery : comes for repatch : ', this.props.data.address.result, selectAddRecordId);

            if (addBarLatLng) {
                const isEligibleResponse = await this.isEligibleForQuickDelivery(addBarLatLng.lat, addBarLatLng.lng);
                const addressObj = this.props.data.address.result?.filter((add: any) => {
                    return Number(add.RecordId) === Number(selectAddRecordId);
                });
                console.log('delivery : comes for repatch addressObj : ', addressObj, isEligibleResponse);
                addressObj && addressObj?.length > 0 && (await this.patchAddressToCart(addressObj[0], isEligibleResponse));
            }
        }
    }

    public componentWillUnmount(): void {
        document.body && document.body.removeEventListener('mousedown', this._focusOutTarget, false);
        window && window.removeEventListener('keyup', this._keyup, false);
    }

    public componentDidUpdate(): void {
        // const {
        //     config: { hideSearchLabel }
        // } = this.props;
        // const { isInitialState } = this.state;

        if (!this.props.context.request.params.isEditor) {
            // this.searchTextInput && this.searchTextInput.current && this.searchTextInput.current.focus();
            // if (!hideSearchLabel && isSearchFormExpanded) {
            //     this.searchTextInput && this.searchTextInput.current && this.searchTextInput.current.focus();
            // } else if (!isInitialState) {
            //     this.searchCollapsedLabel && this.searchCollapsedLabel.current && this.searchCollapsedLabel.current.focus();
            // }
        }
    }

    public shouldComponentUpdate(nextProps: ISearchProps<ISearchData>, nextState: ISearchState): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    // eslint-disable-next-line complexity -- ignore complexity.
    public render(): JSX.Element {
        const {
            context,
            resources: { cancelBtnAriaLabel, searchBtnAriaLabel, submitBtnAriaLabel },
            config: { hideSearchLabel, disableSubmitSearch, searchplaceholderText, shouldShowFullCategoryPath, className = '' }
        } = this.props;

        const {
            searchKeywordSuggestClass,
            searchProductSuggestClass,
            searchCategorySuggestClass,
            suggestions,
            searchRefiners,
            isLoadingAutoSuggest
        } = this.state;

        // Save search text in state
        let searchKeyword = '';
        if (MsDyn365.isBrowser && window.location.pathname && window.location.pathname === '/search') {
            let urlParams = window.location.search;
            urlParams = urlParams.replace('?', ''); // remove the ?
            searchKeyword = urlParams && urlParams.replace('q=', '');
        }
        const searchText = this.state.searchText && this.state.searchText !== '' ? this.state.searchText : searchKeyword;
        //

        const searchQsp = (context && context.app && context.app.config && context.app.config.searchQueryStringParameter) || 'q';
        const searchPageURL = getUrlSync('search', context && context.actionContext) || '';
        const searchURL = this._appendQueryParams(searchPageURL, searchText, searchQsp);
        const searchPlaceholder =
            searchplaceholderText && searchplaceholderText.length > 0 ? searchplaceholderText : "icon Search in 'product name'";

        const keywordSuggestions = suggestions?.AllSearchResults?.filter(
            suggestion => suggestion.SuggestionType === SuggestionType.Keyword || suggestion.SuggestionType === SuggestionType.None
        );
        const productSuggestions = suggestions?.AllSearchResults?.filter(
            suggestion => suggestion.SuggestionType === SuggestionType.Product
        );
        const categoryRefiners = searchRefiners?.find(refiner => {
            return refiner.SourceValue === ProductRefinerSource.Category;
        });
        const categorySuggestions = suggestions?.AllSearchResults?.filter(suggestion => {
            if (suggestion.SuggestionType !== SuggestionType.Category) {
                return false;
            }
            const refiner = categoryRefiners?.Values?.find(item => {
                return `${item.RefinerRecordId ?? 0}` === suggestion.Id;
            });

            return !ObjectExtensions.isNullOrUndefined(refiner);
        });
        const viewProps: ISearchViewProps = {
            ...(this.props as ISearchProps<ISearchData>),

            Search: {
                moduleProps: this.props,
                className: `${classnames('ms-search', className)} ${disableSubmitSearch ? 'no-submit' : 'with-submit'}`
            },
            AutoSuggestAriaLabel: {
                tag: 'div',
                className: 'msc-autoSuggest__screen-reader',
                'aria-live': 'assertive',
                ref: this.autoSuggestResultScreenReaderDiv
            },
            // languageSelector: <LanguageSelector />,
            addressBarDetails: this.state.addressBarDetails,
            setAddressBarDetails: this.setAddressBarDetails,
            activeModalContent: this.state.activeModalContent,
            setActiveModalContent: this.setActiveModalContent,
            isModalOpen: this.state.isModalOpen,
            toggleMapModal: this.toggleMapModal,
            setSelectedMarker: this.setSelectedMarker,
            setSelectedAddress: this.setSelectedAddress,
            isEligibleForQuickDelivery: this.isEligibleForQuickDelivery,
            setAddressLatLng: this.setAddressLatLng,
            addressLatLng: this.state.addressLatLng,
            selectedAddress: this.state.selectedAddress,
            isEligibleForQD: this.state.isEligibleForQD,
            mapApiKey: this.state.mapApiKey,
            selectedMarker: this.state.selectedMarker,
            AutoSuggestAriaLabelText: this.state.autoSuggestAriaLabel,
            searchText: this.state.searchText,
            AutoSuggest: {
                ref: this.autoSuggestResultDiv,
                className: 'ms-search__autoSuggest'
            },
            KeywordSuggest: {
                className: searchKeywordSuggestClass
            },
            ProductSuggest: {
                className: searchProductSuggestClass
            },
            CategorySuggest: {
                className: searchCategorySuggestClass
            },
            SearchForm: {
                className: `${this.searchFormClass} bx-show`
            },
            FormWrapper: {
                tag: 'form',
                ref: this.formReference,
                className: 'ms-search__searchForm',
                'aria-label': 'Search',
                name: 'searchForm',
                role: 'search',
                action: searchURL,
                autoComplete: 'off',
                onSubmit: this._handleSubmit
            },
            UlKeyword: {
                tag: 'ul',
                className: classnames('msc-autoSuggest__keywordResults-items', className)
            },
            UlProduct: {
                tag: 'ul',
                className: classnames('msc-autoSuggest__productResults-items', className)
            },
            UlCategory: {
                tag: 'ul',
                className: classnames('msc-autoSuggest__categoryResults-items', className)
            },
            // label: LabelComponent({
            //     isSearchFormExpanded,
            //     searchLabelClass: `${this.searchLabelClass} ${isSearchFormExpanded ? 'bx-hide' : 'bx-show'}`,
            //     searchtext,
            //     searchLabelArialLabel,
            //     searchCollapsedLabel: this.searchCollapsedLabel,
            //     hideSearchLabel,
            //     handleCancelSearchChange: this._handleCancelSearchChange,
            //     telemetryContent: this.telemetryContent
            // }),
            form: FormComponent({
                ariaLabelCancel: cancelBtnAriaLabel,
                ariaLabelSearch: searchBtnAriaLabel,
                ariaLabelSubmit: submitBtnAriaLabel,
                hideSearchLabel,
                searchTextInput: this.searchTextInput,
                disableSubmitSearch,
                handleInputChange: this._handleInputChange,
                handleCancelSearchChange: this._handleCancelSearchChange,
                maxChars: this.maxChars,
                searchQsp,
                searchPlaceholder,
                query: searchText
            }),
            autosuggestKeyword:
                searchText &&
                searchText.length > 0 &&
                (isLoadingAutoSuggest || (keywordSuggestions && ArrayExtensions.hasElements(keywordSuggestions)))
                    ? KeywordSuggestionsComponent({
                          searchKeywordSuggestClass,
                          searchPageURL,
                          searchQsp,
                          hitPrefix: this.hitPrefix,
                          hitSuffix: this.hitSuffix,
                          autoSuggestProps: this.props,
                          searchText,
                          suggestions: keywordSuggestions,
                          telemetryContent: this.telemetryContent
                      })
                    : undefined,
            autosuggestProduct:
                searchText &&
                searchText.length > 0 &&
                (isLoadingAutoSuggest || (productSuggestions && ArrayExtensions.hasElements(productSuggestions)))
                    ? ProductSuggestionsComponent({
                          searchProductSuggestClass,
                          searchPageURL,
                          searchQsp,
                          hitPrefix: this.hitPrefix,
                          hitSuffix: this.hitSuffix,
                          autoSuggestProps: this.props,
                          searchText,
                          suggestions: productSuggestions,
                          telemetry: this.props.telemetry,
                          moduleType: this.props.typeName,
                          moduleId: this.props.id,
                          telemetryContent: this.telemetryContent,
                          resources: this.props.resources
                      })
                    : undefined,
            autosuggestCategory:
                searchText &&
                searchText.length > 0 &&
                (isLoadingAutoSuggest || (categorySuggestions && ArrayExtensions.hasElements(categorySuggestions)))
                    ? CategorySuggestionsComponent({
                          searchCategorySuggestClass,
                          searchPageURL,
                          searchQsp,
                          hitPrefix: this.hitPrefix,
                          hitSuffix: this.hitSuffix,
                          autoSuggestProps: this.props,
                          searchText,
                          suggestions: categorySuggestions,
                          searchRefiners,
                          telemetryContent: this.telemetryContent,
                          shouldShowFullPath: shouldShowFullCategoryPath
                      })
                    : undefined,
            callbacks: {
                handleCancelSearchChange: this._handleCancelSearchChange,
                handleCancelSearchFocused: this._handleCancelSearchFocused
            },
            isLoadingAutoSuggest,
            isLoadingNode: this._isLoadingNode(),
            polygonsData: this.state.polygonsData,
            cart: this.props.data?.cart?.result,
            userToken: this.props.context.request.user.token
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public async fetchPolygonData() {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const axios = require('axios');

        const config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: 'https://api.localy.ae/api/polygon-details',
            // url: 'https://apidev.localy.ae/api/polygon-details',
            headers: {
                Authorization:
                    'Bearer 04a4630dad889c91385e805388950275356073672364ce30181e3e1fd3530310b27cef9e4673743823f421b30d35b04ef6cd1cc77c87f26d3cacdfc6f5959f995d1120cea4eb9ba632bd107f1481e060f61c9a3e7f5b48ab6815f93064f52edb9d0be1556208c9a557e4e7498f1129af249e357e7ef43982881fd8fe45e85124'
            }
        };

        return axios
            .request(config)
            .then((response: any) => {
                if (response?.data) {
                    // Filter only available cords by status
                    const allCordsArr =
                        response.data?.data &&
                        response.data?.data?.length > 0 &&
                        response.data?.data.filter((polygon: any) => {
                            return polygon?.attributes.status === true;
                        });
                    console.log('all cords allCordsArr : ', allCordsArr);
                    this.setState({ polygonsData: allCordsArr });
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    public async patchAddressToCart(addressObj: any, QDEligibility: boolean): Promise<void> {
        if (
            this.props.data?.cart?.result?.cart?.CartLines &&
            this.props.data?.cart?.result?.cart?.CartLines?.length > 0 &&
            this.props.data?.cart?.result?.cart?.Id &&
            this.props.data?.cart?.result?.cart?.Id !== ''
        ) {
            const deliveryType = QDEligibility ? 'QD_CC' : 'FREE';
            const cartItems = this.props.data?.cart?.result?.cart?.CartLines.map((val: any) => {
                return {
                    LineId: val.LineId,
                    DeliverySpecification: {
                        DeliveryModeId: deliveryType,
                        DeliveryPreferenceTypeValue: 1,
                        DeliveryAddress: addressObj
                    }
                };
            });
            console.log('this.props.isEligibleForQD : ', deliveryType, QDEligibility);

            try {
                const payload = { lineDeliverySpecifications: cartItems };
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const axios = require('axios');
                const config = {
                    method: 'post',
                    maxBodyLength: Infinity,
                    url: `https://api.localy.ae/cart/${this.props.data?.cart?.result?.cart?.Id}/UpdateLineDeliverySpecifications?api-version=7.3`,
                    // url: `https://apidev.localy.ae/cart/${this.props.data?.cart?.result?.cart?.Id}/UpdateLineDeliverySpecifications?api-version=7.3`,
                    headers: {
                        Accept: 'application/json',
                        Authorization: `id_token ${this.props.context.request.user.token}`,
                        'Cache-Control': 'no-cache',
                        oun: '00000001',
                        'Content-Type': 'application/json'
                    },
                    data: payload
                };
                await axios
                    .request(config)
                    .then(async (response: any) => {
                        await this.patchCartAttributes();
                    })
                    .catch((error: any) => {});
            } catch (error) {
                // Handle error
            }
        }
    }

    public async patchCartAttributes() {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const axios = require('axios');
        const data = JSON.stringify({
            AttributeValues: [
                {
                    '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                    Name: 'Order Channel Type',
                    TextValue: 'Web',
                    ExtensionProperties: [],
                    TextValueTranslations: []
                }
            ]
        });

        const config = {
            method: 'patch',
            maxBodyLength: Infinity,
            // url: `https://apidev.localy.ae/cart/${this.props.data?.cart?.result?.cart?.Id}?api-version=7.3`,
            url: `https://api.localy.ae/cart/${this.props.data?.cart?.result?.cart?.Id}?api-version=7.3`,
            headers: {
                Accept: 'application/json',
                Authorization: `id_token ${this.props.context.request.user.token}`,
                'Content-Type': 'application/json',
                'accept-language': 'en-US',
                oun: '00000001',
                prefer: 'return=representation'
            },
            data: data
        };

        axios
            .request(config)
            .then((response: any) => {
                this.props.data?.cart?.result?.refreshCart;
                // console.log('address repatch cart attr response', JSON.stringify(response.data));
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    // Haversine formula in JavaScript
    public fetchDistance(lat: number, lon: number, currentLat: number, currentLng: number) {
        const R = 6371; // Radius of the earth in km
        const dLat = (lat - currentLat) * (Math.PI / 180);
        const dLon = (lon - currentLng) * (Math.PI / 180);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((currentLat * Math.PI) / 180) * Math.cos((lat * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    private isEligibleForQuickDelivery = async (lat: number, lng: number) => {
        let isEligible = false;
        const loader = new Loader({
            apiKey: 'AIzaSyDv7IRtei-JwJykFjK8BaOg-pt_CrWUmvs',
            version: 'weekly',
            libraries: ['geometry', 'places'],
            language: 'en',
            region: 'AE'
        });
        isEligible = await loader.load().then(async () => {
            const city =
                this.props.data?.cart?.result?.cart?.ShippingAddress && this.props.data?.cart?.result?.cart?.ShippingAddress?.City !== ''
                    ? this.props.data?.cart?.result?.cart?.ShippingAddress.City
                    : 'Dubai';
            const QDCordsArr =
                this.state.polygonsData &&
                this.state.polygonsData?.length > 0 &&
                this.state.polygonsData.filter((polygon: any) => {
                    return polygon?.attributes.city === city && polygon?.attributes.type === 'QD';
                });
            const allQDCoordinatesResult =
                (QDCordsArr &&
                    QDCordsArr.length > 0 &&
                    QDCordsArr.map(item => {
                        const dataString = JSON.parse(item.attributes.data);
                        const polygonData = new google.maps.Polygon({
                            paths: dataString
                        });
                        const newmapser = new google.maps.LatLng(lat, lng);
                        let result = true;
                        // Checking if the point is within given polygons bounds
                        if (google.maps.geometry.poly.containsLocation(newmapser, polygonData)) {
                            // user is in allowed bounds
                            result = true;
                        } else {
                            result = false;
                        }
                        return { id: item.id, boundsResult: result };
                    })) ||
                [];
            // Check if user is inside any polygons bound
            const isInsideQDBoundsResult =
                allQDCoordinatesResult &&
                allQDCoordinatesResult.filter((i: any) => {
                    return i?.boundsResult === true;
                });
            this.setQuickDeliveryEligibility(isInsideQDBoundsResult && isInsideQDBoundsResult?.length > 0 ? true : false);
            return isInsideQDBoundsResult && isInsideQDBoundsResult?.length > 0 ? true : false;
        });
        return isEligible;
    };

    private _initSuggestionTypes() {
        const suggestionTypes = this.props.config.suggestionTypeCriterion;

        const suggestions = ArrayExtensions.unique(ArrayExtensions.validValues(suggestionTypes));
        if (!ArrayExtensions.hasElements(suggestions)) {
            this.inputSuggestionType = [
                { SuggestionType: SuggestionTypeCriterionSuggestionType.keyword },
                { SuggestionType: SuggestionTypeCriterionSuggestionType.product },
                { SuggestionType: SuggestionTypeCriterionSuggestionType.scopedCategory }
            ];
            return;
        }

        const validSuggestions = suggestions.filter(suggestion => suggestion.SuggestionType);
        if (ArrayExtensions.hasElements(validSuggestions)) {
            this.inputSuggestionType = validSuggestions;
            return;
        }

        this.inputSuggestionType = [];
    }

    /**
     * Function to create the ReactNode for loading Autosuggest result.
     * @returns ReachNode for loading Autosuggest result.
     */
    private readonly _isLoadingNode = (): React.ReactNode => {
        const loadingText = this.props.resources.autoSuggestResultLoadingMessage;
        return <Spinner className='msc-autoSuggest__loadingResult' label={loadingText} size={SpinnerSize.medium} labelPosition='right' />;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly _focusOutTarget = (e: any): void => {
        const { suggestions } = this.state;

        if (suggestions && suggestions.AllSearchResults && e.target.closest('.ms-search') === null) {
            this._clearAutoSuggestState();
        }
    };

    private readonly _keyup = (e: KeyboardEvent): void => {
        const { suggestions } = this.state;

        if (suggestions && suggestions.AllSearchResults && e.keyCode === KeyCodes.Escape) {
            this._clearAutoSuggestState();
        }

        if (
            !(
                (this.formReference.current && this.formReference.current.contains((e.target as unknown) as Node)) ||
                (this.autoSuggestResultDiv.current && this.autoSuggestResultDiv.current.contains((e.target as unknown) as Node))
            )
        ) {
            if (suggestions && suggestions.AllSearchResults) {
                this._clearAutoSuggestState();
            }
        }
    };

    private readonly _clearAutoSuggestState = (): void => {
        const {
            context: { actionContext },
            data: { searchState }
        } = this.props;

        if (this.searchTextInput.current && this.searchTextInput.current.value.length > 0) {
            searchState.searchText = '';
            searchState.defaultBasicSearchUrl = '';
            searchState.searchQueryStringParameter = '';
            actionContext ? actionContext.update(createSearchStateInput(searchState), searchState) : null;
            this.searchTextInput.current.value = '';
        }

        this.setState({ searchText: '', suggestions: undefined, searchRefiners: undefined });
    };

    private async toggleMapModal(): Promise<void> {
        this.setState({ isModalOpen: !this.state.isModalOpen });
    }

    private async setAddressBarDetails(data: string): Promise<void> {
        this.setState({ addressBarDetails: data });
        localStorage.setItem('address-bar-details', data);
        //Remove this
        localStorage.setItem('address-refreshed-status', 'refreshed');
    }

    private async setAddressLatLng(data: any): Promise<void> {
        this.setState({ addressLatLng: data });
        localStorage.setItem('address-lat-lng', data);
    }

    private async setQuickDeliveryEligibility(data: boolean) {
        this.setState({ isEligibleForQD: data });
    }

    private async setActiveModalContent(data: string): Promise<void> {
        this.setState({ activeModalContent: data });
    }
    private async setSelectedAddress(data: any): Promise<void> {
        localStorage.setItem('selected-address', data);
        this.setState({ selectedAddress: data });
    }
    private async setSelectedMarker(marker: any): Promise<void> {
        await this.isEligibleForQuickDelivery(marker.lat, marker.lng);
        this.setAddressLatLng(JSON.stringify({ lat: marker.lat, lng: marker.lng }));
        localStorage.setItem('delivery-location', JSON.stringify(marker));
        this.setState({ selectedMarker: marker });
        void this.setAddressBarDetails(marker?.address_name !== '' ? marker?.address_name : marker?.name);
        void this.setActiveModalContent('location');
        localStorage.removeItem('selected-address');
    }

    private async _getSearchSuggestions(loadSuggestionsRequestId: string, query: string) {
        try {
            const searchSuggestions = await getSearchSuggestionsAction(
                new AutoSuggestInput(query, this.topResultsCount, this._getSuggestionType(), this.hitPrefix, this.hitSuffix),
                this.props.context.actionContext
            );
            if (loadSuggestionsRequestId !== this.loadSuggestionsRequestId) {
                return undefined;
            }
            return searchSuggestions;
        } catch (error) {
            if (loadSuggestionsRequestId !== this.loadSuggestionsRequestId) {
                return undefined;
            }
            this.setState({ suggestions: undefined, searchRefiners: undefined, autoSuggestAriaLabel: this._setAutoSuggestResultCount() });
            this.props.telemetry.exception(error);
            this.props.telemetry.debug('Unable to get auto suggest results');
            return undefined;
        }
    }

    private async _updateSearchRefiners(loadSuggestionsRequestId: string, query: string, suggestions: Autosuggestions | undefined) {
        try {
            const refiners = await this._getSearchRefiners(query);
            if (loadSuggestionsRequestId !== this.loadSuggestionsRequestId) {
                return;
            }
            this.setState({
                suggestions,
                searchRefiners: refiners,
                autoSuggestAriaLabel: this._setAutoSuggestResultCount(suggestions),
                isLoadingAutoSuggest: false
            });
        } catch (error) {
            if (loadSuggestionsRequestId !== this.loadSuggestionsRequestId) {
                return;
            }
            this.setState({ suggestions, searchRefiners: undefined, autoSuggestAriaLabel: this._setAutoSuggestResultCount(suggestions) });
            this.props.telemetry.exception(error);
            this.props.telemetry.debug('Unable to get refiners results');
        }
    }

    private _loadSuggestions(query: string) {
        this.cancellabelDebounce = debounce(async () => {
            this.setState({ isLoadingAutoSuggest: true });
            const loadSuggestionsRequestId = Random.Guid.generateGuid();
            this.loadSuggestionsRequestId = loadSuggestionsRequestId;
            const searchSuggestions = await this._getSearchSuggestions(loadSuggestionsRequestId, query);
            await this._updateSearchRefiners(loadSuggestionsRequestId, query, searchSuggestions);
        }, this.waitTime);

        setTimeout(() => {
            this.cancellabelDebounce();
        }, 0);
    }

    private readonly _handleInputChange = (query: string): void => {
        if (!query || query.length <= this.maxChars) {
            this.setState({ searchText: query });
        }

        if (this.cancellabelDebounce) {
            this.cancellabelDebounce.cancel();
        }

        const {
            context,
            data: { searchState }
        } = this.props;

        if (context) {
            if (query && query.length > 0) {
                if (query.length > this.maxChars) {
                    return;
                }

                if (ArrayExtensions.hasElements(this.inputSuggestionType)) {
                    this._loadSuggestions(query);
                }

                searchState.searchText = query;
            } else {
                this.setState({ suggestions: undefined, searchRefiners: undefined });
                searchState.searchText = '';
            }
            context.actionContext.update(createSearchStateInput(searchState), searchState);
        }
    };

    private _getSuggestionType(): string {
        const distinctSuggestionTypes: string[] = this.inputSuggestionType.map(
            suggestionTypeItem => suggestionTypeItem.SuggestionType!.charAt(0).toUpperCase() + suggestionTypeItem.SuggestionType!.slice(1)
        );
        return distinctSuggestionTypes.join(',');
    }

    private async _getSearchRefiners(searchText: string): Promise<ProductRefiner[]> {
        const { actionContext: ctx } = this.props.context;
        const catalogId = getCatalogId(ctx.requestContext);
        validateCatalogId(catalogId);
        return ProductsDataActions.getProductSearchRefinersAsync(
            { callerContext: ctx },
            {
                SearchCondition: searchText,
                Context: {
                    ChannelId: +ctx.requestContext.apiSettings.channelId,
                    CatalogId: catalogId
                }
            }
        );
    }

    private readonly _handleSubmit = (
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>
    ): void => {
        const {
            config: { disableSubmitSearch },
            context: {
                actionContext,
                app: {
                    config: { searchQueryStringParameter }
                }
            }
        } = this.props;

        const query = this.searchTextInput.current && this.searchTextInput.current.value;
        e.preventDefault();

        if (!query || (query && query.length === 0) || disableSubmitSearch) {
            this.searchTextInput && this.searchTextInput.current && this.searchTextInput.current.focus();
        } else {
            const searchQsp = searchQueryStringParameter || 'q';
            const searchURL = this._appendQueryParams(getUrlSync('search', actionContext) || '', query, searchQsp);
            document.location.href = searchURL;
        }
    };

    private readonly _handleCancelSearchChange = (): void => {
        const { suggestions } = this.state;

        if (suggestions && suggestions.AllSearchResults) {
            this._clearAutoSuggestState();
        }
    };

    private readonly _handleCancelSearchFocused = (): void => {
        const { suggestions } = this.state;
        if (!(suggestions && suggestions.AllSearchResults)) {
            this._clearAutoSuggestState();
        }
    };

    private readonly _appendQueryParams = (route: string, query: string, qsp: string): string => {
        if (!MsDyn365.isBrowser) {
            return '';
        }

        const queryUrl = new URL(route, window.location.href);
        if (qsp && query) {
            queryUrl.searchParams.set(qsp, query);
        }

        return queryUrl.href;
    };

    private readonly _setAutoSuggestResultCount = (result?: Autosuggestions) => {
        if (this.autoSuggestResultScreenReaderDiv.current !== null) {
            const ariaLiveRegion = this.autoSuggestResultScreenReaderDiv.current;
            const suggestResultString = this.props.resources.autoSuggestFoundMessage;
            const emptyResultString = this.props.resources.noAutoSuggestionMessage;
            ariaLiveRegion.innerText =
                result && result.AllSearchResults && result.AllSearchResults.length > 0 ? suggestResultString : emptyResultString;
            ariaLiveRegion.setAttribute('aria-live', 'assertive');
            return ariaLiveRegion.innerText;
        }
        return '';
    };
}

export default Search;
